import React, { useState } from 'react';
import {
    TrendingUp, TrendingDown, Package, Users,
    Receipt, Wallet, ArrowRight, PieChart as ChartIcon,
    Download, Filter, Briefcase
} from 'lucide-react';
import { StatCard } from '../components/StatCard';

const ProjectPNL = () => {
    const [selectedProject] = useState({
        name: "Villa Renovation - Virar",
        contract: 1200000,
        material: 450000,
        labour: 120000,
        other: 30000
    });

    const totalExpense = selectedProject.material + selectedProject.labour + selectedProject.other;
    const netProfit = selectedProject.contract - totalExpense;
    const margin = ((netProfit / selectedProject.contract) * 100).toFixed(1);

    return (
        <div className="space-y-8">
            {/* Header with Project Selector */}
            <div className="flex justify-between items-end">
                <div>
                    <div className="flex items-center gap-2 text-indigo-600 mb-2">
                        <Briefcase size={16} />
                        <span className="text-[10px] font-black uppercase tracking-[0.2em]">Project Intelligence</span>
                    </div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic">
                        {selectedProject.name}
                    </h1>
                </div>
                <div className="flex gap-3">
                    <button className="bg-white border border-slate-200 px-6 py-4 rounded-2xl font-bold text-slate-600 flex items-center gap-2 hover:bg-slate-50 transition-all">
                        <Filter size={18} /> Switch Project
                    </button>
                    <button className="bg-slate-900 text-white px-6 py-4 rounded-2xl font-bold shadow-xl flex items-center gap-2">
                        <Download size={18} /> P&L Statement
                    </button>
                </div>
            </div>

            {/* Top Level Health Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard title="Total Contract" value={`₹${(selectedProject.contract / 100000).toFixed(1)}L`} sub="Invoiced Revenue" color="bg-indigo-600" icon={<Receipt size={20} />} />
                <StatCard title="Total Expenses" value={`₹${(totalExpense / 100000).toFixed(1)}L`} sub="Material + Labour" color="bg-rose-500" icon={<TrendingDown size={20} />} />
                <StatCard title="Net Profit" value={`₹${(netProfit / 100000).toFixed(1)}L`} sub={`${margin}% Margin`} color="bg-emerald-600" icon={<TrendingUp size={20} />} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                {/* EXPENSE BREAKDOWN LEDGER */}
                <div className="bg-white rounded-[48px] border border-slate-100 p-10 shadow-sm">
                    <h3 className="text-xl font-black text-slate-900 mb-8 flex items-center gap-2">
                        <ChartIcon size={20} className="text-indigo-600" /> Cost Allocation
                    </h3>

                    <div className="space-y-6">
                        <div className="p-6 bg-slate-50 rounded-3xl group hover:bg-indigo-50 transition-all">
                            <div className="flex justify-between items-center mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-3 bg-white rounded-2xl text-indigo-600 shadow-sm"><Package size={20} /></div>
                                    <span className="font-bold text-slate-700">Material Costs</span>
                                </div>
                                <span className="font-black text-slate-900 text-lg">₹{selectedProject.material.toLocaleString()}</span>
                            </div>
                            <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
                                <div className="bg-indigo-500 h-full w-[65%]"></div>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-2 font-bold uppercase">Plywood, Hardware, Laminates</p>
                        </div>

                        <div className="p-6 bg-slate-50 rounded-3xl group hover:bg-emerald-50 transition-all">
                            <div className="flex justify-between items-center mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-3 bg-white rounded-2xl text-emerald-600 shadow-sm"><Users size={20} /></div>
                                    <span className="font-bold text-slate-700">Labour & Workforce</span>
                                </div>
                                <span className="font-black text-slate-900 text-lg">₹{selectedProject.labour.toLocaleString()}</span>
                            </div>
                            <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
                                <div className="bg-emerald-500 h-full w-[25%]"></div>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-2 font-bold uppercase">Daily Wages, Site Attendance</p>
                        </div>
                    </div>
                </div>

                {/* RECENT TRANSACTIONS LINKED TO THIS PROJECT */}
                <div className="bg-slate-900 rounded-[48px] p-10 text-white shadow-2xl overflow-hidden relative">
                    <div className="absolute top-0 right-0 p-10 opacity-10"><Wallet size={100} /></div>
                    <h3 className="text-xl font-black mb-8 italic relative z-10">Recent Project Activity</h3>

                    <div className="space-y-4 relative z-10">
                        <div className="flex items-center justify-between p-5 bg-white/5 rounded-2xl border border-white/10">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-rose-500/20 text-rose-400 rounded-xl flex items-center justify-center font-black">V</div>
                                <div>
                                    <p className="text-sm font-bold">Plywood Plaza Bill</p>
                                    <p className="text-[10px] text-slate-500 uppercase">Vendor Purchase</p>
                                </div>
                            </div>
                            <p className="font-black text-rose-400">-₹45,000</p>
                        </div>

                        <div className="flex items-center justify-between p-5 bg-white/5 rounded-2xl border border-white/10">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-emerald-500/20 text-emerald-400 rounded-xl flex items-center justify-center font-black">C</div>
                                <div>
                                    <p className="text-sm font-bold">Designer AD Payment</p>
                                    <p className="text-[10px] text-slate-500 uppercase">Client Revenue</p>
                                </div>
                            </div>
                            <p className="font-black text-emerald-400">+₹2,00,000</p>
                        </div>
                    </div>

                    <button className="w-full mt-8 py-5 border-2 border-dashed border-white/20 rounded-3xl text-sm font-black uppercase tracking-widest hover:border-indigo-400 hover:text-indigo-400 transition-all">
                        View Full Project Ledger
                    </button>
                </div>

            </div>
        </div>
    );
};

export default ProjectPNL;